// 入札一覧の絞り込みの保持 (AC-SC-031、 #12745)。 両タブで共有する売/買・ステータス・ワークフロー
// ([[SharedBidListFilter]]) と、 タブ固有の絞り込み (通常入札タブの売買逆転のみ・入札制限超過のみ、
// ブロック入札タブの種別・提出状態) を page が 1 つの値で持ち、 1 つの key に保存する。 タブ固有の項目は
// そのタブの一覧にだけ効く (他のタブには渡さない)。 CSV 取込の着地で広げる判定も page の 1 回にまとまる。
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import {
  bool,
  defineFilterCodec,
  nonEmptyText,
  oneOf,
} from '~/models/filterCodec'

import { readonlySetOf } from './readonlySetField'
import {
  EMPTY_SHARED_BID_LIST_FILTER,
  type SharedBidListFilter,
  widenSharedBidListFilterForCsvImport,
} from './sharedBidListFilter'
import { SPOT_BID_STATUS_FILTER_CATEGORIES } from './spotBidStatusCategory'

/** ブロック入札タブの種別。 '' = すべて、 以降は親種別 (= STANDARD / LINK / LOOP) 単位。 */
export type BlockKindFilter = '' | 'STANDARD' | 'LINK' | 'LOOP'
/** ブロック入札タブの提出ステータス。 '' = すべて、 submitted = 提出済、 unsubmitted = 未提出。 */
export type BlockSubmitFilter = '' | 'submitted' | 'unsubmitted'

const BLOCK_KIND_FILTER_VALUES: readonly BlockKindFilter[] = [
  '',
  'STANDARD',
  'LINK',
  'LOOP',
]
const BLOCK_SUBMIT_FILTER_VALUES: readonly BlockSubmitFilter[] = [
  '',
  'submitted',
  'unsubmitted',
]

/** 通常入札タブ固有の絞り込み (アラートの「売買逆転のみ表示」・「違反のみ」)。 */
export type NormalBidTabListFilters = {
  inversionOnly: boolean
  limitOnly: boolean
}

/** ブロック入札タブ固有の絞り込み。 */
export type BlockBidTabListFilters = {
  blockKind: BlockKindFilter
  blockSubmit: BlockSubmitFilter
}

export type SpotBidListFilters = SharedBidListFilter &
  NormalBidTabListFilters &
  BlockBidTabListFilters

/** 入札一覧の絞り込みを保存する localStorage の key (通常入札タブ・ブロック入札タブで 1 つ)。 */
export const SPOT_BID_LIST_FILTERS_STORAGE_KEY =
  'ebalance-spot-bid-list-filters'

const DEFAULT_TAB_LIST_FILTERS: NormalBidTabListFilters &
  BlockBidTabListFilters = {
  inversionOnly: false,
  limitOnly: false,
  blockKind: '',
  blockSubmit: '',
}

/**
 * 売/買・ステータスだけを保存していた形式 (タブ固有の項目が無い) も読める (欠けた項目は既定)。
 * 項目の追加は互換のある変更なので key は上げない。
 */
export const spotBidListFiltersCodec = defineFilterCodec<SpotBidListFilters>({
  defaults: () => ({
    ...EMPTY_SHARED_BID_LIST_FILTER,
    ...DEFAULT_TAB_LIST_FILTERS,
  }),
  fields: {
    sides: readonlySetOf(oneOf<Side>([Side.BUY, Side.SELL])),
    statuses: readonlySetOf(oneOf(SPOT_BID_STATUS_FILTER_CATEGORIES)),
    workflows: readonlySetOf(nonEmptyText()),
    inversionOnly: bool(),
    limitOnly: bool(),
    blockKind: oneOf(BLOCK_KIND_FILTER_VALUES),
    blockSubmit: oneOf(BLOCK_SUBMIT_FILTER_VALUES),
  },
})

/**
 * 入札 CSV を取り込んで移ってきた時に、 取り込んだ札が一覧に出るよう絞り込みを広げる (AC-SPOT-024)。
 * 共有の売/買・ステータス・ワークフローは今の選択に足す ([[widenSharedBidListFilterForCsvImport]])。
 *
 * タブ固有の絞り込みは既定 (売買逆転のみ・入札制限超過のみは OFF、 種別・提出状態は「すべて」) に戻す。
 * 取り込んだ札は入札制限を超えず (違反は取込全体を拒否)、 承認待ちにも提出済にもなり、 種別もどれでも
 * ありうるので、 絞ったままだと隠れるため。 spec (spot-bid-normal-csv-import.md AC-SPOT-024) の
 * 列挙 (エリア・売/買・コマ・ステータス・ワークフロー) に無い項目の広げ方なので仮採用で、 PdM 確認予定。
 *
 * 変えるものが無ければ undefined (呼び出し側は保存し直さない)。
 */
export function widenBidListFiltersForCsvImport(
  filters: SpotBidListFilters,
  importedSides: ReadonlyArray<'buy' | 'sell'>,
): SpotBidListFilters | undefined {
  const shared = widenSharedBidListFilterForCsvImport(filters, importedSides)
  const isTabFilterDefault =
    filters.inversionOnly === DEFAULT_TAB_LIST_FILTERS.inversionOnly &&
    filters.limitOnly === DEFAULT_TAB_LIST_FILTERS.limitOnly &&
    filters.blockKind === DEFAULT_TAB_LIST_FILTERS.blockKind &&
    filters.blockSubmit === DEFAULT_TAB_LIST_FILTERS.blockSubmit
  if (shared === undefined && isTabFilterDefault) return undefined
  return {
    sides: shared?.sides ?? filters.sides,
    statuses: shared?.statuses ?? filters.statuses,
    workflows: shared?.workflows ?? filters.workflows,
    ...DEFAULT_TAB_LIST_FILTERS,
  }
}
